const createFormStore = (initialValues, validators, config) => {
  const fields = {};
  const values = {};
  const fieldOrder = [];
  const errors = {};
  const formState = {};
  const fieldWatchers = new Map();
  const formStateWatchers = new Set();
  const fieldEffects = new Map();
  let isInitializing = true;

  const registerEffect = (deps, callback) => {
    const unsubscribers = [];

    deps.forEach((field) => {
      if (!fieldEffects.has(field)) {
        fieldEffects.set(field, new Set());
      }

      fieldEffects.get(field).add(callback);

      unsubscribers.push(() => {
        fieldEffects.get(field)?.delete(callback);
      });
    });

    callback(values);

    return () => {
      unsubscribers.forEach((fn) => fn());
    };
  };

  const runEffects = (changedField) => {
    const effects = fieldEffects.get(changedField);

    if (effects) {
      effects.forEach((fn) => fn(values));
    }
  };

  const subscribeField = (name, cb) => {
    if (!fieldWatchers.has(name)) {
      fieldWatchers.set(name, new Set());
    }

    fieldWatchers.get(name).add(cb);

    return () => {
      fieldWatchers.get(name)?.delete(cb);
    };
  };

  const subscribeFormState = (cb) => {
    formStateWatchers.add(cb);

    return () => {
      formStateWatchers.delete(cb);
    };
  };

  const notifyField = (name) => {
    const subs = fieldWatchers.get(name);
    if (!subs) return;
    subs.forEach((cb) => cb(values[name]));
  };

  const notifyFormState = (name) => {
    const subs = formStateWatchers;
    if (!subs) return;
    subs.forEach((cb) => cb());
  };

  const setError = (name, value) => {
    if (validators) {
      if (validators[name]) {
        errors[name] = validators[name](value, values, config);
      }
    }
  };

  const setValue = (name, value) => {
    values[name] = value;
    setError(name, value);
    updateFormState();
    if (!isInitializing) {
      runEffects(name);
      notifyField(name);
      notifyFormState();
    }
  };

  const getValue = (name) => values[name];

  const getField = (name) => fields[name];

  const getValues = () => values;

  const getFields = () => fields;

  const getFieldOrder = () => fieldOrder;

  const registerField = (name, el, identificador) => {
    if (!fields[name]) {
      fieldOrder.push(name);
    }
    fields[name] = {
      el,
      disabled: false,
      group: 'default',
      priority: 1,
      identificador,
    };
  };

  const setInitialValues = () => {
    for (const name in initialValues) {
      values[name] = initialValues[name];
    }
    for (const name in initialValues) {
      setValue(name, initialValues[name]);
    }
  };

  setInitialValues();
  isInitializing = false;

  function updateFormState() {
    const errors2 = Object.keys(errors);
    let isError = false;
    for (let i = 0; i < errors2.length && !isError; i++) {
      if (errors[errors2[i]] !== '') {
        isError = true;
      }
    }
    formState.isError = isError;
  }

  const getFormState = () => {
    formState.errors = errors;
    formState.values = values;
    return formState;
  };

  function unregisterField(nome) {
    delete fields[nome];

    const index = fieldOrder.indexOf(nome);
    if (index !== -1) {
      fieldOrder.splice(index, 1);
    }
  }

  const setFieldState = (name, state) => {
    if (!fields[name]) return;

    fields[name] = {
      ...fields[name],
      ...state,
    };
  };

  return {
    setValue,
    getValue,
    getValues,
    registerField,
    getFormState,
    getFields,
    getField,
    subscribeField,
    unregisterField,
    setFieldState,
    getFieldOrder,
    subscribeFormState,
    registerEffect,
  };
};

export default createFormStore;
